import React, { Component } from 'react'
import { Link, Redirect } from 'react-router-dom'

import { localizer, languages } from '../../../Translator'
import { docsGroups, findDocById, DEFAULT_DOC_ID } from './docsConfig'
import { getDocsContentTranslation, getDocsLabelTranslation } from './docsContentTranslations'
import { Markdown } from './Markdown'
import './docs.css'

const normalizeDocsHosts = (content) => content.replace(
  /https:\/\/(?:pre-aws\.postamail\.es|web\.postamail\.es)(?=\/)/g,
  'https://<your-postamail-domain>'
)

/**
 * API documentation site.
 * Content itself lives in ./content/*.js (see docsConfig.js) so adding/editing pages never
 * requires touching this layout.
 */
export class DocsLayout extends Component {
  constructor(props) {
    super(props)
    const savedTheme = window.localStorage.getItem('docs-theme')
    this.state = {
      theme: savedTheme === 'dark' || savedTheme === 'light'
        ? savedTheme
        : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
      searchTerm: '',
      culture: window.localStorage.getItem('culture') || 'en',
      languageMenuOpen: false,
      pageHeadings: [],
      activeHeading: null
    }
  }

  handleSearchChange = (event) => {
    this.setState({ searchTerm: event.target.value })
  }

  handleLanguageChange = (culture) => {
    if (culture === this.state.culture) {
      this.setState({ languageMenuOpen: false })
      return
    }

    window.localStorage.setItem('culture', culture)
    window.location.reload(false)
  }

  toggleLanguageMenu = () => {
    this.setState(previousState => ({ languageMenuOpen: !previousState.languageMenuOpen }))
  }

  toggleTheme = () => {
    const theme = this.state.theme === 'dark' ? 'light' : 'dark'
    window.localStorage.setItem('docs-theme', theme)
    this.setState({ theme })
  }

  localize = (key, fallback) => {
    return key && localizer[key] ? localizer[key] : fallback
  }

  resolveContent = (doc) => {
    const content = doc.content
    const language = localizer.getLanguage ? localizer.getLanguage() : 'en'
    const translated = getDocsContentTranslation(doc.id, language)
    const resolved = translated || (typeof content === 'string' ? content : content[language] || content.en || '')
    return doc.id === 'introduction' ? resolved : normalizeDocsHosts(resolved)
  }

  localizeChildTitle = (title) => {
    const language = localizer.getLanguage ? localizer.getLanguage() : 'en'
    return getDocsLabelTranslation(title, language) || title
  }

  componentDidMount() {
    document.body.setAttribute('data-docs-theme', this.state.theme)
    this.updatePageHeadings()
    this.scrollToHash()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.theme !== this.state.theme) {
      document.body.setAttribute('data-docs-theme', this.state.theme)
    }
    if (prevProps.location !== this.props.location) {
      if (prevProps.location.pathname !== this.props.location.pathname) {
        this.updatePageHeadings()
        if (this.contentElement) this.contentElement.scrollTop = 0
      }
      this.scrollToHash()
    }
  }

  componentWillUnmount() {
    document.body.removeAttribute('data-docs-theme')
  }

  updatePageHeadings = () => {
    const headings = this.articleElement
      ? Array.from(this.articleElement.querySelectorAll('.docs-h2, .docs-h3, .docs-h4'))
      : []
    this.headingElements = headings
    this.setState({
      pageHeadings: headings.map(heading => ({ id: heading.id, title: heading.textContent, level: Number(heading.tagName.slice(1)) })),
      activeHeading: headings.length > 0 ? headings[0].id : null
    })
  }

  handleContentScroll = () => {
    if (!this.contentElement || !this.headingElements || !this.headingElements.length) return
    const top = this.contentElement.getBoundingClientRect().top + 40
    let activeHeading = this.headingElements[0].id
    this.headingElements.forEach(heading => {
      if (heading.getBoundingClientRect().top <= top) activeHeading = heading.id
    })
    if (activeHeading !== this.state.activeHeading) this.setState({ activeHeading })
  }

  scrollToHash = () => {
    const hash = window.location.hash ? window.location.hash.substring(1) : null
    if (!hash) return
    window.setTimeout(() => {
      const target = document.getElementById(hash)
      if (target) target.scrollIntoView({ block: 'start' })
    }, 0)
  }

  getChildHref = (item, child) => {
    return '/docs/api/' + item.id + '#' + child.anchor
  }

  render() {
    const pageId = this.props.match && this.props.match.params ? this.props.match.params.pageId : null

    if (!pageId) {
      return <Redirect to={'/docs/api/' + DEFAULT_DOC_ID} />
    }

    const activeDoc = findDocById(pageId)
    const searchTerm = this.state.searchTerm.trim().toLowerCase()
    const selectedLanguage = languages.find(language => language.code === this.state.culture) || languages[0]
    const language = localizer.getLanguage ? localizer.getLanguage() : 'en'
    const contentsTitle = language === 'es' ? 'En esta página' : language === 'pt' ? 'Nesta página' : 'On this page'
    const darkModeLabel = language === 'es' ? 'Modo oscuro' : language === 'pt' ? 'Modo escuro' : 'Dark mode'

    return (
      <React.Fragment>
        <header className="docs-appbar">
          <Link to="/docs/api" className="docs-appbar-brand">
            <img src="images/postamail_logo.png" alt="PostaMail Logo" />
            <span>{this.localize('DocsSidebarTitle', 'PostaMail API')}</span>
          </Link>
          <div className="docs-language-selector">
            <span className="docs-visually-hidden" id="docs-language-label">{this.localize('Language', 'Language')}</span>
            <button
              type="button"
              className="docs-language-current"
              aria-haspopup="listbox"
              aria-expanded={this.state.languageMenuOpen}
              aria-labelledby="docs-language-label"
              onClick={this.toggleLanguageMenu}
            >
              <img src={'images/' + selectedLanguage.code + '.svg'} alt="" />
              <span>{selectedLanguage.name}</span>
            </button>
            {this.state.languageMenuOpen && (
              <div className="docs-language-menu" role="listbox" aria-labelledby="docs-language-label">
                {languages.map(language => (
                  <button
                    type="button"
                    role="option"
                    aria-selected={language.code === this.state.culture}
                    className={'docs-language-option' + (language.code === this.state.culture ? ' docs-language-option-active' : '')}
                    key={language.code}
                    onClick={() => this.handleLanguageChange(language.code)}
                  >
                    <img src={'images/' + language.code + '.svg'} alt="" />
                    <span>{language.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="docs-theme-toggle"
            aria-label={darkModeLabel}
            aria-pressed={this.state.theme === 'dark'}
            title={darkModeLabel}
            onClick={this.toggleTheme}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              {this.state.theme === 'dark'
                ? <React.Fragment><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l2 2m10 10l2 2M5 19l2-2M17 7l2-2" /></React.Fragment>
                : <path d="M21 13a9 9 0 0 1-10-10A9 9 0 1 0 21 13z" />}
            </svg>
          </button>
          <a
            href="/openapi/postamail-platform.json"
            className="docs-appbar-openapi"
            download="postamail-platform-openapi.json"
          >
            <img src="images/openapi.svg" alt="" aria-hidden="true" />
            <span>{this.localize('DocsDownloadOpenApi', 'Download OpenAPI')}</span>
          </a>
          <Link to="/login" className="docs-appbar-login">
            {this.localize('LogIn', 'LOG IN')}
          </Link>
        </header>
        <div className="docs-container">
        <nav className="docs-sidebar">
          <div className="docs-sidebar-heading">
            <div className="docs-sidebar-title">{this.localize('DocsSidebarTitle', 'PostaMail API')}</div>
            <div className="docs-sidebar-subtitle">{this.localize('DocsSidebarSubtitle', 'Integration guide')}</div>
          </div>
          <input
            type="text"
            className="docs-search-input"
            placeholder={this.localize('DocsSearchPlaceholder', 'Search docs...')}
            value={this.state.searchTerm}
            onChange={this.handleSearchChange}
          />
          {docsGroups.map(group => {
            const visibleItems = group.items.filter(item => !searchTerm || this.localize(item.titleKey, item.title).toLowerCase().includes(searchTerm))
            if (visibleItems.length === 0) return null
            return (
              <div key={group.group}>
                <div className="docs-sidebar-group-title">{this.localize(group.groupKey, group.group)}</div>
                {visibleItems.map(item => (
                  <React.Fragment key={item.id}>
                    <Link
                      to={'/docs/api/' + item.id}
                      className={'docs-sidebar-link' + (item.id === pageId ? ' docs-sidebar-link-active' : '')}
                    >
                      {this.localize(item.titleKey, item.title)}
                    </Link>
                    {item.children && item.id === pageId && item.children.map(child => (
                      <Link
                        key={child.anchor}
                        to={this.getChildHref(item, child)}
                        className="docs-sidebar-sublink"
                        onClick={this.scrollToHash}
                      >
                        <span>{this.localizeChildTitle(child.title)}</span>
                        {child.method && <span className={'docs-sidebar-method docs-sidebar-method-' + child.method.toLowerCase()}>{child.method}</span>}
                      </Link>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            )
          })}
        </nav>
        <main className="docs-content" ref={element => { this.contentElement = element }} onScroll={this.handleContentScroll}>
          <div className={'docs-page' + (this.state.pageHeadings.length ? ' docs-page-with-index' : '')}>
            <article className="docs-article" ref={element => { this.articleElement = element }}>
              {activeDoc ? (
                <Markdown source={this.resolveContent(activeDoc)} />
              ) : (
                <p className="docs-paragraph">{this.localize('DocsPageNotFound', 'This page does not exist. Pick a topic from the sidebar.')}</p>
              )}
            </article>
            {this.state.pageHeadings.length > 0 && (
              <nav className="docs-page-index" aria-label={contentsTitle}>
                <div className="docs-page-index-title">{contentsTitle}</div>
                {this.state.pageHeadings.map((heading, index) => (
                  <Link key={heading.id + '-' + index} to={'/docs/api/' + pageId + '#' + heading.id}
                    onClick={this.scrollToHash}
                    aria-current={this.state.activeHeading === heading.id ? 'location' : undefined}
                    className={'docs-page-index-link docs-page-index-level-' + heading.level +
                      (this.state.activeHeading === heading.id ? ' docs-page-index-link-active' : '')}>
                    {heading.title}
                  </Link>
                ))}
              </nav>
            )}
          </div>
        </main>
        </div>
      </React.Fragment>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/docs/api/DocsLayout.js